import { watch, type Ref } from "vue";
import type { VeeForm } from "@/types/vee-validate";

/**
 * Compose un {@code watch} qui re-declenche la validation du champ
 * confirm d'une paire "mot de passe / confirmation" ( pattern
 * vee-validate {@code confirmed:source} ) des que la SOURCE change .
 *
 * <p><b>Pourquoi</b> : la regle vee-validate {@code confirmed} ne
 * s'execute QUE lorsque le champ confirm change , PAS lorsque la
 * source change . Sans ce hook , modifier le nouveau mot de passe
 * APRES avoir saisi la confirmation laisse le champ confirm marque
 * "valide" ( coche verte ) alors que les valeurs ne matchent plus -
 * tres perturbant et trompeur .
 *
 * <p><b>Usage</b> :
 * <pre>{@code
 * const newPassword = useText().refText;
 * const observer = ref<VeeForm | null>(null);
 * useCrossFieldConfirmation(
 *   newPassword,
 *   observer,
 *   "change_password.confirm_new_password"
 * );
 * }</pre>
 *
 * <p>Le composable n'attend PAS la promesse de {@code validateField}
 * ( {@code void} cast ) : le watch declenche en arriere-plan et le
 * resultat est consume par vee-validate via le slot {@code errors}
 * du composant {@code <Field>} . Aucun besoin d'attendre cote caller .
 *
 * <p>Symetrique inverse non necessaire ( watch sur le confirm pour
 * re-valider la source ) : la regle {@code confirmed} est sur le
 * confirm , donc se redeclenche naturellement au changement du
 * confirm .
 *
 * @param source        ref reactif sur la valeur de la source ( ex le
 *                      nouveau mot de passe )
 * @param observer      ref vers la balise {@code <Form ref="observer">}
 * @param confirmField  nom du champ confirm tel que declare dans l'attribut
 *                      {@code name} du {@code <TextField>} ( ex
 *                      "change_password.confirm_new_password" )
 */
export function useCrossFieldConfirmation(
  source: Ref<string>,
  observer: Ref<VeeForm | null>,
  confirmField: string,
): void {
  watch(source, () => {
    void observer.value?.validateField(confirmField);
  });
}
